import { useCallback, useEffect, useMemo, useState } from "react";
import SliderWithThumb from "../../components/SliderWithThumb";
import { Link, useNavigate, useParams } from "react-router-dom";
import CustomTitle from "../../components/CustomTitle";
import MailingList from "../../components/MailingList";
import Footer from "../../components/layouts/Footer";
import PlaceYourBidModal from "../../components/PlaceYourBidModal";
import OpenDisputeModal from "../../components/OpenDisputeModal";
import AboutSeller from "../../components/AboutSeller";
import SellerInfo from "../../components/SellerInfo";
import { ArtworkCardProps } from "../../types";
import {
  useAdDetails,
  useSellerAggregate,
  useSimilarAds,
  useMyBids,
  useActiveDispute,
  useAddToWatchlist,
  useRemoveFromWatchlist,
} from "../../hooks/queries";
import { usePersistStore, useStore } from "../../store";
import { Utils } from "../../utils";
import SwipperMultiple from "../../components/SwipperMultiple";
import { AD_STATUS } from "../../utils/enums";
import CommonModal from "../../components/CommonModal";
import BidUpdatePopup from "../../components/BidUpdatePopup";
import { AdStatusChip } from "../../components/status";
import SliderWithThumbVertical from "../../components/SliderWithThumbVertical";
import ImageLightbox from "../../components/ImageLightbox";
import useAdUtils from "../../hooks/useAdUtils";
import useBreakpoint from "../../hooks/useBreakpoint";
import { setRedirectToCurrentLocation } from "../../utils/postLoginRedirect";
import { Skeleton } from "boneyard-js/react";

function Details() {
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    token,
    user,
    watchlist,
    currentLocation,
    isVerifiedUser,
    isBuyerProfileCompleted,
  } = usePersistStore((s: any) => s);
  const { setErrorMessage, setSuccessMessage, setInfoMessage } = useStore(
    (s: any) => s
  );
  const [showBidAlreadyExists, setShowBidAlreadyExists] = useState(false);
  const [showPlaceYourBidModel, setShowPlaceYourBidModel] = useState(false);
  const [showDisputeModel, setShowDisputeModel] = useState(false);
  const [showGetVerifiedFirst, setShowGetVerifiedFirst] = useState(false);
  const [isInWatchlist, setIsInWatchlist] = useState<boolean>(false);
  const [showUpdatePopup, setShowUpdatePopup] = useState(false);
  // Touch fallback for the gallery hover loupe: when a tap lands on the
  // main image (no `hover` capability available), open the existing
  // fullscreen lightbox so the user can still inspect the image.
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);

  // ── Cached reads ───────────────────────────────────────────────────────
  const {
    data: details,
    isLoading: detailsLoading,
    error: detailsError,
  } = useAdDetails(id, { userId: token && user ? user?._id : "" }, token || "");
  const ad = details?.ad ?? null;
  const seller = details?.seller ?? null;
  const isReady = !detailsLoading;
  const slidesData = useMemo(
    () => (ad?.images || []).map((img: string) => ({ img })),
    [ad]
  );

  const { data: sellerAggregate = null } = useSellerAggregate(seller?._id);

  const {
    data: similarData,
    isLoading: similarLoading,
    error: similarError,
  } = useSimilarAds(
    {
      categoryId: ad?.categoryId?._id,
      subCategoryId: ad?.subCategoryId?._id,
      mediumId: ad?.mediumId?._id,
      location: currentLocation,
    },
    !!ad
  );
  const isSimilarReady = !!ad && !similarLoading;
  const similarProducts: ArtworkCardProps[] = useMemo(
    () =>
      (similarData || [])
        .filter((tt: any) => tt?._id !== ad?._id)
        .map((tt: any) => ({
          _id: tt?._id,
          color: true,
          imageUrl: tt?.images?.[0],
          category: tt?.categoryId?.name,
          subCategory: tt?.subCategoryId?.name,
          views: tt?.views || 0,
          title: tt?.title,
          status: tt?.status,
          ownerImage: tt?.userId?.fileUrl,
          ownerName: tt?.userId?.name,
          ownerId: tt?.userId?._id,
          biddingStarts: tt?.biddingStarts,
          biddingEnds: tt?.biddingEnds,
          price: tt?.price,
          showBidNowButton: true,
          distance: tt?.distance,
        })),
    [similarData, ad]
  );

  const {
    data: myBids,
    error: bidsError,
    refetch: refetchMyBids,
  } = useMyBids(ad && token ? token : undefined);
  const bidDetails = useMemo(
    () => myBids?.bids?.find((k: any) => k?.adId?._id === ad?._id) ?? null,
    [myBids, ad]
  );
  const bidAlreadyExists = !!bidDetails;

  const {
    data: disputeData,
    error: disputeError,
    refetch: refetchDispute,
  } = useActiveDispute(id, token);
  const hasActiveDispute = disputeData?.hasActive || false;

  // Surface any read error through the global toast (matches prior behavior).
  useEffect(() => {
    const e = detailsError || similarError || bidsError || disputeError;
    if (e) setErrorMessage((e as Error).message);
  }, [detailsError, similarError, bidsError, disputeError, setErrorMessage]);

  // Keep the watchlist heart in sync with the persisted watchlist (the
  // add/remove handlers also toggle it optimistically).
  useEffect(() => {
    if (id) setIsInWatchlist(!!watchlist?.includes(id));
  }, [id, watchlist]);

  const addToWatchlist = useAddToWatchlist();
  const removeFromWatchlist = useRemoveFromWatchlist();

  // Watchlist writes go through the shared mutations (store update + toast +
  // cache invalidation live in the hook); we keep only the optimistic toggle.
  const handleAddToWatchlist = useCallback(
    (e: any) => {
      e?.preventDefault();
      if (!id) return;
      if (!token) {
        setRedirectToCurrentLocation();
        navigate("/login");
        return;
      }
      setIsInWatchlist(true);
      addToWatchlist.mutate(id, { onError: () => setIsInWatchlist(false) });
    },
    [id, token, navigate, addToWatchlist]
  );

  const handleRemoveFromWatchlist = useCallback(
    (e: any) => {
      e?.preventDefault();
      if (!id) return;
      if (!token) {
        setRedirectToCurrentLocation();
        navigate("/login");
        return;
      }
      setIsInWatchlist(false);
      removeFromWatchlist.mutate(id, { onError: () => setIsInWatchlist(true) });
    },
    [id, token, navigate, removeFromWatchlist]
  );

  const { isOpenForBids, isClosed, isUpcoming, isComingSoon } = useAdUtils(ad);
  const breakpoint = useBreakpoint();

  const middleCrumb: { label: string; href?: string } | null = isOpenForBids
    ? { label: "Open for bids", href: "/explore/open-for-bids" }
    : isUpcoming
      ? { label: "Upcoming bids", href: "/explore/upcoming" }
      : isComingSoon
        ? { label: "Coming soon" }
        : isClosed
          ? { label: "Closed bids" }
          : ad?.status === AD_STATUS.SOLD
            ? { label: "Sold" }
            : null;

  return (
    <>
      <div className="container">
        <div className="row">
          <div className="breadCrumb mt-5 mb-3">
            <div className="d-flex flex-column flex-md-row gap-3 justify-content-between align-items-start align-items-md-center">
              <div className="d-flex flex-wrap gap-3" style={{ minWidth: 0 }}>
                <Link to="/">
                  <p className="m-0">Home</p>
                </Link>
                <span>/</span>
                {middleCrumb && (
                  <>
                    {middleCrumb.href ? (
                      <Link to={middleCrumb.href}>
                        <p className="m-0">{middleCrumb.label}</p>
                      </Link>
                    ) : (
                      <p className="m-0">{middleCrumb.label}</p>
                    )}
                    <span>/</span>
                  </>
                )}
                <h4 className="mb-0 text-truncate detailsBreadcrumbTitle">
                  {ad?.title}
                </h4>
              </div>
              {token &&
                user &&
                ad &&
                ad?.userId?._id !== user?._id &&
                ad?.status !== AD_STATUS.SOLD &&
                !hasActiveDispute && (
                  <button
                    className="disputeBtn flex-shrink-0 align-self-end align-self-md-auto"
                    onClick={() =>
                      isVerifiedUser
                        ? setShowDisputeModel(true)
                        : setShowGetVerifiedFirst(true)
                    }
                  >
                    Open a dispute
                  </button>
                )}
            </div>
          </div>
        </div>
        {/* Wrap the entire details body in a single boneyard skeleton.
            CLI captures the full layout from a real /details/:id render
            (loading=false). At runtime, while the ad is loading, the
            same wrapper renders the captured bones. The min-height
            keeps the wrapper from collapsing while children are empty. */}
        <Skeleton name="details-page" loading={!isReady}>
          <div style={{ minHeight: 800 }}>
        {isReady && (
          <div className="row mb-5">
            <div className="col px-md-0 px-3">
              <div className="row pt-4 gx-md-5 adDetails">
                <div className="col-md-7">
                  {breakpoint === "desktop" ? (
                    <SliderWithThumbVertical
                      slidesData={slidesData}
                      enableMagnifier
                      onMainImageTap={(src) => setLightboxImage(src)}
                    />
                  ) : (
                    <SliderWithThumb
                      slidesData={slidesData}
                      dir="horizontal"
                      enableMagnifier
                      onMainImageTap={(src) => setLightboxImage(src)}
                    />
                  )}
                </div>
                <div className="col-md-5 mt-md-0 mt-4">
                  <div className="adDetails__panel">
                    <div className="adDetails__topRow">
                      {isOpenForBids && (
                        <span className="adDetails__status adDetails__status--live">
                          <span className="adDetails__statusDot" /> Live · closing{" "}
                          {Utils.formatFromNow(ad?.biddingEnds)}
                        </span>
                      )}
                      {isComingSoon && (
                        <span className="adDetails__status adDetails__status--comingSoon">
                          Coming Soon
                          {ad?.biddingStarts ? (
                            <> · opens {Utils.formatFromNow(ad?.biddingStarts)}</>
                          ) : null}
                        </span>
                      )}
                      {isUpcoming && (
                        <span className="adDetails__status adDetails__status--upcoming">
                          Opens {Utils.formatFromNow(ad?.biddingStarts)}
                        </span>
                      )}
                      {isClosed && (
                        <span className="adDetails__status adDetails__status--closed">
                          Closed
                        </span>
                      )}
                      {ad?.categoryId?.name && (
                        <span className="adDetails__categoryChip">
                          {ad?.categoryId?.name}
                          {ad?.subCategoryId?.name
                            ? ` · ${ad?.subCategoryId?.name}`
                            : ""}
                        </span>
                      )}
                    </div>

                    <h1 className="adDetails__title">{ad?.title}</h1>

                    <div className="adDetails__viewsRow">
                      <span>
                        <i className="bi bi-eye" aria-hidden="true" />
                        {parseInt(ad?.views) === 0
                          ? 1
                          : Utils.formatToNumber(ad?.views)}{" "}
                        views
                      </span>
                      {ad?.code && (
                        <span className="adDetails__code" title="Reference code">
                          <i className="bi bi-hash" aria-hidden="true" />
                          {ad.code}
                        </span>
                      )}
                    </div>

                    <div className="adDetails__seller">
                      <SellerInfo seller={seller} />
                    </div>

                    {ad?.description && (
                      <p className="adDetails__description">
                        {ad?.description}
                      </p>
                    )}

                    <div className="adDetails__metaGrid">
                      {ad?.mediumId?.name && (
                        <div className="adDetails__metaTile">
                          <div className="adDetails__metaLabel">Medium</div>
                          <div className="adDetails__metaValue">
                            {ad?.mediumId?.name}
                          </div>
                        </div>
                      )}
                      <div className="adDetails__metaTile">
                        <div className="adDetails__metaLabel">Dimensions</div>
                        <div className="adDetails__metaValue">
                          {ad?.height}″ × {ad?.width}″ × {ad?.depth}″
                        </div>
                      </div>
                      <div className="adDetails__metaTile">
                        <div className="adDetails__metaLabel">Weight</div>
                        <div className="adDetails__metaValue">
                          {ad?.weight} lb
                        </div>
                      </div>
                      <div className="adDetails__metaTile">
                        <div className="adDetails__metaLabel">Bids open</div>
                        <div className="adDetails__metaValue">
                          {Utils.formatDateTimeInTz(
                            ad?.biddingStarts,
                            user?.timezone || ad?.timezone
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="adDetails__priceBlock">
                      <div className="adDetails__priceLeft">
                        <div className="adDetails__priceLabel">Opening bid</div>
                        <div className="adDetails__priceValue">
                          {Utils.formatToCurrency(ad?.price)}
                        </div>
                      </div>
                      <div className="adDetails__priceRight">
                        <div className="adDetails__priceLabel">
                          {isClosed ? "Bid closed" : "Closes"}
                        </div>
                        <div className="adDetails__priceMeta">
                          {Utils.formatDateTimeInTz(
                            ad?.biddingEnds,
                            user?.timezone || ad?.timezone
                          )}
                        </div>
                      </div>
                    </div>

                    {isOpenForBids && ad?.userId?._id === user?._id && (
                      <div className="adDetails__notice">
                        <i className="bi bi-hammer" aria-hidden="true" />
                        <span>
                          {(ad?.bidsCount ?? 0) === 0
                            ? "No bids on your artwork yet — it'll show up here as collectors start bidding."
                            : `${ad?.bidsCount} ${ad?.bidsCount === 1 ? "collector has" : "collectors have"} already placed a bid on your artwork.`}
                        </span>
                      </div>
                    )}

                    {ad?.status !== AD_STATUS.ON_SALE &&
                      !isComingSoon &&
                      !isClosed && (
                        <div className="adDetails__statusInline">
                          <span className="adDetails__metaLabel">Status</span>
                          <AdStatusChip status={ad?.status} />
                        </div>
                      )}

                    {ad?.userId?._id !== user?._id && (
                      <div className="adDetails__actions">
                        {!bidAlreadyExists && isOpenForBids && (
                          <button
                            type="button"
                            className="adDetails__primary"
                            onClick={() => {
                              if (!token) {
                                setRedirectToCurrentLocation();
                                navigate("/login");
                                return;
                              }
                              if (!isBuyerProfileCompleted) {
                                setInfoMessage(
                                  "Please complete your profile first."
                                );
                                return;
                              }
                              if (bidAlreadyExists) {
                                setShowBidAlreadyExists(true);
                                return;
                              }
                              setShowPlaceYourBidModel(true);
                            }}
                          >
                            Bid Now
                          </button>
                        )}
                        {bidAlreadyExists && isOpenForBids && (
                          <button
                            type="button"
                            className="adDetails__primary"
                            onClick={() => {
                              if (!token) {
                                setRedirectToCurrentLocation();
                                navigate("/login");
                                return;
                              }
                              setShowUpdatePopup(true);
                            }}
                          >
                            Update your bid
                          </button>
                        )}
                        {(isOpenForBids || isUpcoming || isComingSoon) && (
                          <button
                            type="button"
                            className={`adDetails__watchlist ${
                              isInWatchlist
                                ? "adDetails__watchlist--active"
                                : ""
                            }`}
                            onClick={
                              isInWatchlist
                                ? handleRemoveFromWatchlist
                                : handleAddToWatchlist
                            }
                            aria-label={
                              isInWatchlist
                                ? "Remove from watchlist"
                                : "Add to watchlist"
                            }
                            title={
                              isInWatchlist
                                ? "Remove from watchlist"
                                : "Add to watchlist"
                            }
                          >
                            <i
                              className={`bi ${
                                isInWatchlist ? "bi-heart-fill" : "bi-heart"
                              }`}
                              aria-hidden="true"
                            />
                            <span>
                              {isInWatchlist ? "Saved" : "Watchlist"}
                            </span>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
          </div>
        </Skeleton>
      </div>

      <div className="container mt-5">
        <Skeleton name="about-seller" loading={!seller}>
          <div className="aboutSellerSkeletonWrap">
            {seller ? (
              <AboutSeller
                seller={seller}
                aggregate={sellerAggregate}
                showViewProfile
              />
            ) : null}
          </div>
        </Skeleton>
      </div>

      {(!isSimilarReady || similarProducts?.length > 0) && (
        <div className="position-relative mb-5">
          <div className="container px-md-0 px-3">
            <div className="py-4">
              <CustomTitle title="Similar products" showAll={false} />
            </div>
            {!isSimilarReady ? (
              <div className="row">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div className="col-12 col-lg-6 col-xl-4 mb-3" key={i}>
                    <Skeleton name="artwork-card" loading={true}>
                      <span style={{ display: "block", aspectRatio: "3 / 4" }} />
                    </Skeleton>
                  </div>
                ))}
              </div>
            ) : (
              <SwipperMultiple
                uniqueId="similar-artworks"
                slidesData={similarProducts}
              />
            )}
          </div>
        </div>
      )}

      {/* <div className="container pt-4">
        <div className="row mx-0">
          <div className="col-md-11 mx-auto mb-4">
            <MailingList />
          </div>
        </div>
      </div> */}
      <Footer />
      {showPlaceYourBidModel && (
        <PlaceYourBidModal
          show={showPlaceYourBidModel}
          onHide={() => setShowPlaceYourBidModel(false)}
          onSuccess={() => {
            setShowPlaceYourBidModel(false);
            refetchMyBids();
            setSuccessMessage("Bid placed successfully.");
          }}
          ad={ad}
          seller={seller}
          slidesData={slidesData}
        />
      )}
      {showDisputeModel && (
        <OpenDisputeModal
          show={showDisputeModel}
          onHide={() => setShowDisputeModel(false)}
          onSuccess={() => refetchDispute()}
        />
      )}
      {showGetVerifiedFirst && (
        <CommonModal
          title="Get Verified"
          message="You need to get verified to perform this action."
          show={showGetVerifiedFirst}
          onHide={() => setShowGetVerifiedFirst(false)}
        />
      )}
      {showBidAlreadyExists && (
        <CommonModal
          title="Bid already placed"
          message="You have already placed your bid on this artwork. If you want to increase the price, you can edit your bid from Bids screen in your dashboard."
          show={showBidAlreadyExists}
          onHide={() => setShowBidAlreadyExists(false)}
        />
      )}
      {showUpdatePopup && (
        <BidUpdatePopup
          show={showUpdatePopup}
          onHide={() => setShowUpdatePopup(false)}
          onSuccess={() => {
            setShowUpdatePopup(false);
            refetchMyBids();
          }}
          data={bidDetails}
        />
      )}
      <ImageLightbox
        src={lightboxImage}
        alt={ad?.title}
        onClose={() => setLightboxImage(null)}
      />
    </>
  );
}

export default Details;
